import { onSetLifeCycleForSessions, onUpdateExtractorSession } from "common/signalR";
import { FlightFromAiSidePanel } from "components/SidePanel/Flight/FlightFromAiSidePanel";
import { FormatArrivalTime } from "flights/lookup/FlightSearchResult";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Message } from "semantic-ui-react";
import { formatTime, formatWrittenDayMonthYear } from "utilities";
import { ContactForExtractorSessionList } from "./ContactForExtractorSessionList";
import { FlightAiRandomLoadingMessages } from "components/RandomLoadingMessages/RandomLoadingMessage";
import { useElementHeight } from "utilities/hookutils";
import { ButtonSecondary } from "components/Atoms/Button";
import { useMutation } from "@tanstack/react-query";

const FlightFromTo = ({ artist, flight }: { artist: server.dto.ArtistSmall, flight: server.dto.ExtractedFlight }) => {
  return <div className="flex-2 flight-parser-session-result-grid">
    <div className="flight-designator">
      <FlightFromAiSidePanel
        extractedFlightId={flight.extractedFlightId}
        artist={artist}
        trigger={<a>{flight.flightDesignator}</a>}
      />
    </div>
    <div className="departs-from">
      <div className="info-text-regular margin-bottom-large">
        {formatWrittenDayMonthYear(flight.departureLocal)}
      </div>
      <div className="subtitle-text-regular margin-bottom-small">
        {flight.departureAirport.name} [{flight.departureAirport.iata || flight.departureAirport.icao || flight.departureAirport.faa}]
      </div>
      <div className="text-sublabel margin-bottom-small">
        {flight.departureAirport.location}
      </div>
    </div>
    <div className="departure-time nowrap info-text-regular">
      {formatTime(flight.departureLocal)}
    </div>
    <div className="arrow">
      <i className="icon-arrow-right small" />
    </div>
    <div className="arrives-at">
      <div className="info-text-regular margin-bottom-large">
        <FormatArrivalTime
          departure={moment.utc(flight.departureLocal)}
          arrival={moment.utc(flight.arrivalLocal)}
        />
      </div>
      <div className="subtitle-text-regular margin-bottom-small">
        {flight.arrivalAirport.name} [{flight.arrivalAirport.iata || flight.arrivalAirport.icao || flight.arrivalAirport.faa}]
      </div>
      <div className="text-sublabel margin-bottom-small">
        {flight.arrivalAirport.location}
      </div>
    </div>
    <div className="passenger">
      <ContactForExtractorSessionList
        contactSmall={flight.passengerContact}
        contactName={flight.passengerName}
      />
    </div>
  </div>;
};

interface Props {
  item: server.dto.ExtractorSession
}

export const ExtractorSessionFlightsTable = (props: Props) => {
  const [item, setItem] = useState(props.item);
  const heightHelper = useElementHeight();

  useEffect(() => {
    const res = onUpdateExtractorSession(newItem => {
      if (newItem.extractorSessionId === item.extractorSessionId) {
        setItem(newItem);
      }
    });

    const res2 = onSetLifeCycleForSessions(newItem => {
      if (newItem.extractorSessionIds.includes(item.extractorSessionId)) {
        setItem(prev => ({ ...prev, lifeCycle: newItem.lifeCycle }));
      }
    });

    return () => {
      res.removeEvent();
      res2.removeEvent();
    }
  }, [item.extractorSessionId]);

  const height = item.lifeCycle === "waitingForProcessing" ? heightHelper.height : undefined;

  const tryAgainMutation = useMutation(window.CoreApi.Extractor.ResendSession);

  return <div style={{ height }} ref={heightHelper.ref}>
    {item.lifeCycle === "waitingForProcessing" && <FlightAiRandomLoadingMessages />}
    {item.lifeCycle === "fatalError" && <div className="flex-column">
      <div className="message ui red flex-column gap">
        <div>There was an error with processing.</div>
        <div>
          <ButtonSecondary
            onClick={() => tryAgainMutation.mutate({ extractorSessionId: item.extractorSessionId })}
            loading={tryAgainMutation.isLoading}
            disabled={tryAgainMutation.isLoading}
            label="Try again"
          />
        </div>
      </div>
    </div>}
    {item.lifeCycle === "waitingForUserAction" && <div className="flex-column gap">
      {item.flights.map(flight => <FlightFromTo key={flight.extractedFlightId} artist={item.artist} flight={flight} />)}
    </div>}
    {item.lifeCycle === "finished" && <div className="flex-column"><Message color="green">
      This flight plan has been processed and no further actions are needed. You can find the flights in the main flight list.
    </Message></div>}
  </div>;
}